import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Hammer, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

const ROLES = [
  { id: 'tradie', label: 'Tradesperson', desc: 'I run a trade business. List free forever.', icon: <Hammer size={22} /> },
  { id: 'agent', label: 'Agency / Strata', desc: 'Real estate agency or strata management. Track compliance on every job.', icon: <Building2 size={22} /> },
];

const AGENCY_TYPES = [
  { id: 'realestate', label: 'Real Estate Agency' },
  { id: 'strata', label: 'Strata Management' },
  { id: 'other', label: 'Other (facilities, body corp, etc.)' },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const claimTradieId = sp.get('claim');
  const [claimTrade, setClaimTrade] = useState(null);
  const [role, setRole] = useState('tradie');
  const [agencyType, setAgencyType] = useState('realestate');
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', company: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (claimTradieId) {
      setRole('tradie');
      api.getTradie(claimTradieId).then((r) => setClaimTrade(r.data)).catch(() => {});
    }
  }, [claimTradieId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const companyStr = role === 'agent'
        ? `${form.company} (${AGENCY_TYPES.find((t) => t.id === agencyType)?.label || ''})`.trim()
        : form.company;
      const u = await register({ ...form, company: companyStr, role });
      toast.success(`G'day ${u.name}, welcome to Trades & Neighbours.`);
      // If they came from an unclaimed listing, attempt to claim it now
      if (claimTradieId && claimTrade && u.role === 'tradie') {
        try {
          await api.claimListing(claimTradieId, claimTrade.business_name);
          toast.success(`${claimTrade.business_name} is now yours — head to your dashboard to add photos and upload compliance docs.`);
        } catch (err) {
          toast.error(`Couldn't auto-claim: ${formatError(err)}. You can claim from the profile page.`);
        }
      }
      navigate(u.role === 'tradie' ? '/tradie' : '/agency');
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="register-page">
      <div className="mx-auto max-w-xl px-6 py-16">
        <Link to="/" className="text-sm text-[#8A8988] hover:text-[#1A362D]">← Back</Link>

        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">Join Trades &amp; Neighbours</div>
          <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A]">
            {claimTrade ? `Claim ${claimTrade.business_name}` : 'Who are you?'}
          </h1>
          <p className="mt-2 text-[#5C5B5A]">
            {claimTrade ? `Sign up as ${claimTrade.business_name} to take ownership of the profile and get the green Verified tick.` : 'Homeowners can browse and call without an account. Accounts are for people who need more.'}
          </p>
          {claimTrade && (
            <div className="mt-4 rounded border-l-4 border-[#B08911] bg-[#FFF8E7] p-4 text-sm text-[#5C5B5A]" data-testid="claim-context">
              <b className="text-[#1C1B1A]">Claiming:</b> {claimTrade.business_name} · {claimTrade.suburb} · {(claimTrade.categories || []).join(', ')}
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="role-picker">
          {ROLES.map((r) => (
            <button key={r.id} type="button" onClick={() => setRole(r.id)}
              data-testid={`role-${r.id}`}
              className={`flex flex-col items-start gap-2 border p-4 text-left transition-colors ${role === r.id ? 'border-[#1A362D] bg-[#1A362D] text-white' : 'border-[#E5E4E0] bg-white text-[#1C1B1A] hover:border-[#1A362D]'}`}
            >
              <span className={role === r.id ? 'text-white' : 'text-[#1A362D]'}>{r.icon}</span>
              <div className="font-heading text-lg font-bold">{r.label}</div>
              <div className={`text-xs ${role === r.id ? 'text-white/80' : 'text-[#5C5B5A]'}`}>{r.desc}</div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name" data-testid="register-name" className="input-editorial" />
          {role === 'agent' && (
            <>
              <div>
                <label className="label-editorial">Agency type</label>
                <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="agency-type-picker">
                  {AGENCY_TYPES.map((t) => (
                    <button key={t.id} type="button" onClick={() => setAgencyType(t.id)}
                      data-testid={`agency-type-${t.id}`}
                      className={`border p-3 text-left text-xs font-semibold transition-colors ${agencyType === t.id ? 'border-[#1A362D] bg-[#1A362D] text-white' : 'border-[#E5E4E0] bg-white text-[#1C1B1A] hover:border-[#1A362D]'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder={agencyType === 'strata' ? 'Strata firm name' : 'Agency name'} data-testid="register-company" className="input-editorial" />
            </>
          )}
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email" data-testid="register-email" className="input-editorial" />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone (optional)" data-testid="register-phone" className="input-editorial" />
          <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Password (min 6 chars)" data-testid="register-password" className="input-editorial" />
          <button type="submit" disabled={busy} data-testid="register-submit" className="btn-primary w-full justify-center disabled:opacity-50">
            {busy ? 'Creating account...' : `Create ${role === 'agent' ? 'agency' : 'tradie'} account`}
          </button>
        </form>

        <p className="mt-6 text-sm text-[#5C5B5A]">Already have an account? <Link to="/login" className="font-semibold text-[#1A362D] underline">Sign in</Link></p>
        <p className="mt-2 text-xs text-[#8A8988]">Just looking for a tradie? <Link to="/search" className="underline">Browse the directory</Link> — no signup needed.</p>
      </div>
    </div>
  );
}
